/** @format */

import { Chain, Environment, Reward, StoredRewards } from '@shared/data-access/interfaces';
import { Observable, of, Subject } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';

import {
	HttpService,
	MyAccountAnalyticsService,
	Store,
	UtilityService
} from '@shared/data-access/services';

import { Injectable, OnDestroy } from '@angular/core';

import { Offer } from '@my-account/data-access/interfaces';
import { RewardsServiceErrorLogKeys } from './rewards-error-log-keys.enum';

@Injectable({
	providedIn: 'root'
})
export class RewardsService implements OnDestroy {
	private rewardsUxRedesignEnabled = false;
	private readonly apiVersion = '3';
	private readonly ngUnsubscribe: Subject<void> = new Subject();

	constructor(
		private readonly chain: Chain,
		private readonly environment: Environment,
		private readonly httpService: HttpService,
		private readonly myAccountAnalyticsService: MyAccountAnalyticsService,
		private readonly store: Store,
		private readonly utilityService: UtilityService
	) {
		this.store
			.select('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(first())
			.subscribe((killswitch: any) => {
				this.rewardsUxRedesignEnabled = this.utilityService.checkKillSwitch(
					'rewardsUxRedesignEnabled',
					killswitch,
					this.chain.chainIdentifierAbbr
				);
			});
	}

	ngOnDestroy() {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	getRewards(activeOnly = true, includeRedemption = false): Observable<StoredRewards> {
		const rewardURL = `${this.environment.baseUrls.athleteService}/v${this.chain.apiVersion}/reward/${this.chain.chainIdentifierAbbr}?activeOnly=${activeOnly}`;
		const rewardURLV3 = `${this.environment.baseUrls.athleteService}/v${this.apiVersion}/reward/${this.chain.chainIdentifierAbbr}?activeOnly=${activeOnly}&includeRedemption=${includeRedemption}`;

		const url = this.rewardsUxRedesignEnabled ? rewardURLV3 : rewardURL;
		return this.httpService
			.get(url, 0, {
				withCredentials: true,
				useApiAuth: true
			})
			.pipe(
				map((result: Reward[]) => {
					result.forEach(
						(reward) =>
							(reward.isExpiringSoon = this.daysUntil(reward.expirationDate) <= 21)
					);
					const storedRewards: StoredRewards = { error: false, rewards: result };
					this.store.set('REWARDS', storedRewards);
					return storedRewards;
				}),
				catchError((err) => {
					this.myAccountAnalyticsService.generateLogError(
						RewardsServiceErrorLogKeys.REWARDS_SERVICE_GET_REWARDS,
						'RewardsService - Get Rewards',
						err
					);
					const storedRewards: StoredRewards = { error: true, rewards: [] };
					this.store.set('REWARDS', storedRewards);
					return of(storedRewards);
				})
			);
	}

	getOffers(): Observable<Offer[]> {
		const url = `${this.environment.baseUrls.athleteService}/v${this.chain.apiVersion}/offers`;

		return this.httpService
			.get(url, 0, {
				withCredentials: true,
				useApiAuth: true
			})
			.pipe(
				map((result: Offer[]) => {
					this.store.set('OFFERS', result);
					return result;
				}),
				catchError((err) => {
					this.myAccountAnalyticsService.generateLogError(
						RewardsServiceErrorLogKeys.REWARDS_SERVICE_GET_OFFERS,
						'RewardsService - Get Offers',
						err
					);

					return of([]);
				})
			);
	}

	daysUntil(d: string) {
		const date = new Date(d);
		const today = new Date();
		const oneDay = 1000 * 60 * 60 * 24;
		return Math.ceil((date.getTime() - today.getTime()) / oneDay);
	}
}
